import React, { useState, useEffect } from "react"
import { Button, Col, Row } from "react-bootstrap"
import { FormikProvider, useFormik } from "formik"
import * as yup from "yup"
import Select from "react-select"
import { PopupQuestion } from "../../../../component/atoms/Popup/index.jsx"
import { UploadComponent } from "../../../../component/index.jsx"
import { UploadIcon } from "../../../../component/atoms/Icons/SVG/upload.jsx"
import { VARIABLE_CONSTANT } from "../../../../constants/VariableConstant.js"
import { UPLOAD } from "../../../../constants/InitTypeConstants.js"
import { resetUrlResponse, uploadAction } from "../../../../redux/actions/MasterDataAction.jsx"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import { getPageDataVerifiedCombobox } from "../../../../redux/actions/MitraAction.jsx"

export const ModalChangeStatus = ({
  show = false,
  onClose = () => {},
  onSubmit = () => {},
  totalFarmer = 0,
  isLoading = false
}) => {
  const dispatch = useDispatch()
  const { General, Mitra, MasterData } = useSelector((state) => state)
  const { t } = useTranslation()

  const [currentStep, setCurrentStep] = useState(1)
  const [fileAttachments, setFileAttachments] = useState([])
  const [listMitra, setListMitra] = useState([])

  const validationSchema = yup.object({
    reason: yup.string().required(t("label_error_required", { label: t("label_reason") })),
    file_contract_url: yup.string().required(t("label_error_required", { label: t("label_document") })),
    new_mitra_id: yup.string().when([], {
      is: () => totalFarmer > 0 && currentStep === 2,
      then: yup.string().required(t("label_error_required", { label: t("label_mitra") })),
      otherwise: yup.string()
    })
  })

  const formik = useFormik({
    initialValues: {
      reason: "",
      file_contract_url: "",
      file_name: "",
      file_size: "",
      new_mitra_id: ""
    },
    validationSchema,
    onSubmit: (values) => {
      onSubmit(values)
      handleClose()
    }
  })

  useEffect(() => {
    if (Mitra?.combobox) {
      setListMitra(
        Mitra?.combobox.map((e) => ({
          label: e?.name + ", " + e?.id,
          value: e?.id
        }))
      )
    }
  }, [Mitra?.combobox])

  useEffect(() => {
    if (!show) {
      formik.resetForm()
      setCurrentStep(1)
      setFileAttachments([])
    }
  }, [show])

  useEffect(() => {
    if (totalFarmer > 0) {
      dispatch(getPageDataVerifiedCombobox())
    }
  }, [totalFarmer])

  useEffect(() => {
    if (MasterData.urlResponseUpload) {
      if (!General.loading) {
        if (MasterData.uploadType === UPLOAD.BUSINESS_DOCUMENT) {
          formik.setFieldValue("file_contract_url", MasterData.urlResponseUpload)
        }
      }

      setTimeout(() => {
        dispatch(resetUrlResponse())
      }, 200)
    }
  }, [MasterData.urlResponseUpload, General.loading, MasterData.uploadType])

  const handleClose = () => {
    formik.resetForm()
    setCurrentStep(1)
    setFileAttachments([])
    onClose()
  }

  const handleNext = () => {
    if (currentStep === 1 && totalFarmer > 0) {
      setCurrentStep(2)
    } else {
      formik.handleSubmit()
    }
  }

  const handleBack = () => {
    setCurrentStep(1)
  }

  const handleFileUpload = (file) => {
    setFileAttachments([file])
    formik.setFieldValue("file_name", file.name)
    formik.setFieldValue("file_size", file.size)
    dispatch(uploadAction({ data: file, type: UPLOAD.BUSINESS_DOCUMENT }))
  }

  const handleDeleteFile = () => {
    setFileAttachments([])
    formik.setFieldValue("file_contract_url", "")
    formik.setFieldValue("file_name", "")
    formik.setFieldValue("file_size", "")
  }

  // Step 1: Reason and Document Upload
  const renderReasonForm = () => (
    <div>
      <div className="mb-3">
        <label>
          {t("input_change_mitra_status_reason")}
          <span className="text-danger ml-1" aria-hidden="true">*</span>
        </label>
        <textarea
          required={true}
          rows={3}
          className={`form-control ${
            formik.touched.reason && formik.errors.reason ? "is-invalid" : ""
          }`}
          name="reason"
          value={formik.values.reason}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          placeholder={t("placeholder_input_reason")}
        />
        {formik.touched.reason && formik.errors.reason && (
          <div className="invalid-feedback d-block">{formik.errors.reason}</div>
        )}
      </div>

      <div className="mb-3">
        <UploadComponent
          label={t("contract_document")}
          required={true}
          name="document_change_status"
          onDeleteFileList={handleDeleteFile}
          fileList={fileAttachments}
          typeUpload={"pdf"}
          editable={true}
          onChange={handleFileUpload}
          placeholder={<UploadIcon />}
          maxSize={VARIABLE_CONSTANT.MAX_SIZE_25_MB}
          maxSizeText={VARIABLE_CONSTANT.MAX_SIZE_25_MB_TEXT}
          isLoading={isLoading}
          customFilePreview={{ top: "calc(65% + 20px - 1.25rem)" }}
        />
        {formik.touched.file_contract_url && formik.errors.file_contract_url && (
          <div className="invalid-feedback d-block">{formik.errors.file_contract_url}</div>
        )}
      </div>

      {/* Buttons for Step 1 */}
      <Row className="justify-content-center mt-4">
        <Col>
          <Button
            className="font-weight-bold w-100"
            variant="outline-secondary"
            block={true}
            onClick={handleClose}>
            {t("button_cancel")}
          </Button>
        </Col>
        <Col>
          <Button
            className="font-weight-bold w-100"
            variant={
              formik.values.reason && (totalFarmer === 0 || formik.values.file_contract_url)
                ? "primary"
                : "secondary"
            }
            disabled={
              !formik.values.reason || (totalFarmer > 0 && !formik.values.file_contract_url)
            }
            block={true}
            onClick={handleNext}>
            {totalFarmer > 0 ? t("button_next") : t("button_submit")}
          </Button>
        </Col>
      </Row>
    </div>
  )

  // Step 2: Mitra Selection (only if totalFarmer > 0)
  const renderMitraSelectionForm = () => (
    <div>
      <div className="mb-3">
        <label>{t("label_select_farmer_mitra_assignment")}</label>
        <Select
          name="new_mitra_id"
          value={listMitra.find((option) => option.value === formik.values.new_mitra_id)}
          onChange={(option) => formik.setFieldValue("new_mitra_id", option?.value || "")}
          onBlur={() => formik.setFieldTouched("new_mitra_id", true)}
          placeholder={t("placeholder_select_one")}
          options={listMitra}
          isClearable
        />
        {formik.touched.new_mitra_id && formik.errors.new_mitra_id && (
          <div className="invalid-feedback d-block">{formik.errors.new_mitra_id}</div>
        )}
      </div>

      {/* Buttons for Step 2 */}
      <Row className="justify-content-center mt-4">
        <Col>
          <Button
            className="font-weight-bold w-100"
            variant="outline-secondary"
            block={true}
            onClick={handleBack}>
            {t("button_previous_kembali")}
          </Button>
        </Col>
        <Col>
          <Button
            className="font-weight-bold w-100"
            variant={formik.values.new_mitra_id ? "primary" : "secondary"}
            disabled={!formik.values.new_mitra_id}
            block={true}
            onClick={handleNext}>
            {t("button_submit")}
          </Button>
        </Col>
      </Row>
    </div>
  )

  const getTitle = () => {
    if (currentStep === 1) {
      return t("upload_contract").toUpperCase()
    }
    return t("messages_title_select_mitra_replacement")
  }

  const getBodyContent = () => {
    if (currentStep === 1) {
      return renderReasonForm()
    }
    return renderMitraSelectionForm()
  }

  return (
    <FormikProvider value={formik}>
      <PopupQuestion
        show={show}
        title={getTitle()}
        bodyCustom={getBodyContent()}
        isUseFooter={false}
        onCancel={handleClose}
        closeButton={false}
        centered={true}
      />
    </FormikProvider>
  )
}
